import { InfoOutlineIcon } from '@chakra-ui/icons';
import {
  Box,
  Skeleton,
  Text,
  Table,
  Thead,
  Tr,
  Th,
  Td,
  Tbody,
  Tooltip,
  useToast,
  Flex,
} from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import * as _ from 'lodash';
import React from 'react';

import {
  AccountTypeGQLType,
  BankAccountWithInstitutionDesignationAndOwnerGQLType,
} from 'types/dashboard-core-gqlTypes.generated.d';

import {
  getUnexpectedErrorMessage,
  dollarNumberWithCommas,
} from 'packages/common-frontend/utils';
import { useGetBankAccountsByCreditFacilityIdQuery } from '~/packages/bankAccounts/requests/bankAccounts.gql.generated';
import { colors } from '~/packages/common-frontend/colors';
import { LabeledDetail } from '~/packages/common-frontend/components/LabeledDetail';
import {
  errorToastConfig,
  errorToastId,
} from '~/packages/common-frontend/constants';

const getConnectionAccounts = (
  bankAccount: BankAccountWithInstitutionDesignationAndOwnerGQLType,
): string[] => {
  const connectionProviders: string[] = [];
  if (bankAccount.tellerAccountId) {
    connectionProviders.push('Teller');
  }

  if (bankAccount.plaidAccountId) {
    connectionProviders.push('Plaid');
  }

  if (!bankAccount.plaidAccountId && !bankAccount.tellerAccountId) {
    connectionProviders.push('None');
  }

  return connectionProviders;
};

const BankAccountRow = ({
  bankAccount,
}: {
  bankAccount: BankAccountWithInstitutionDesignationAndOwnerGQLType;
}) => {
  const {
    ownerName,
    institutionName,
    routingNumber,
    accountNumber,
    accountType,
    plaidAccountId,
    tellerAccountId,
    current,
    available,
  } = bankAccount;

  const currentBalance =
    (plaidAccountId || tellerAccountId) && current
      ? dollarNumberWithCommas(current)
      : '-';
  const availableBalance =
    (plaidAccountId || tellerAccountId) && available
      ? dollarNumberWithCommas(available)
      : '-';

  const routingNumberLastFourDigits = routingNumber.substring(
    routingNumber.length - 4,
  );

  const accountNumberLastFourDigits = accountNumber.substring(
    accountNumber.length - 4,
  );

  const accountTypeKeyToNameConfig: {
    [key in AccountTypeGQLType]: string;
  } = {
    collections: 'Collections account',
    otherReserveAccount: 'Other reserve account',
    parentOperatingAccount: 'Operating account',
    preFundingAccount: 'Pre-funding account',
    capitalProviderFundingAccount: 'Funding account',
    permittedInvestmentsAccount: 'Permitted investments account',
    accountIncludedInBorrowingBase: 'Account included in borrowing base',
  };

  return (
    <Tr className="bank-account-row">
      <Td>{ownerName}</Td>
      <Td>{`${institutionName} (${routingNumberLastFourDigits})`}</Td>
      <Td>{`${bankAccount.name} (${accountNumberLastFourDigits})`}</Td>
      <Td>{accountTypeKeyToNameConfig[accountType]}</Td>
      <Td whiteSpace="pre-wrap">
        {_.map(getConnectionAccounts(bankAccount), (connectionProvider) => {
          if (plaidAccountId || tellerAccountId) {
            return (
              <React.Fragment
                key={`${bankAccount.connectionProviderName} (${bankAccount.connectionProviderAccountMask})`}
              >
                {`${connectionProvider} `}
                <Tooltip
                  key={`key-${Math.random()}`}
                  placement="top"
                  label={`${bankAccount.connectionProviderName} (${bankAccount.connectionProviderAccountMask})`}
                >
                  <InfoOutlineIcon />
                </Tooltip>
              </React.Fragment>
            );
          }
          return `${connectionProvider}\n`;
        })}
      </Td>
      <Td>{currentBalance}</Td>
      <Td>{availableBalance}</Td>
    </Tr>
  );
};

export const CreditFacilityBankAccountsContainer = ({
  borrowerId,
  creditFacilityId,
  creditFacilityNumber,
  creditFacilityName,
}: {
  borrowerId: string;
  creditFacilityId: string;
  creditFacilityNumber: number;
  creditFacilityName: string;
}) => {
  const toast = useToast();

  const {
    data: bankAccountsWithDesignationAndOwnerData,
    loading: bankAccountsWithDesignationAndOwnerLoading,
  } = useGetBankAccountsByCreditFacilityIdQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    fetchPolicy: 'no-cache',
    variables: {
      organizationId: borrowerId,
      creditFacilityId,
    },
  });

  if (
    bankAccountsWithDesignationAndOwnerLoading ||
    !bankAccountsWithDesignationAndOwnerData ||
    !bankAccountsWithDesignationAndOwnerData.bankAccountsWithDesignationAndOwner
  ) {
    return <Skeleton data-testid="loading-bank-accounts-view" height="600px" />;
  }

  const sortedBankAccountsWithDesignationAndOwnerData = _.sortBy(
    bankAccountsWithDesignationAndOwnerData.bankAccountsWithDesignationAndOwner,
    ['ownerType', 'accountType'],
  );

  if (
    !bankAccountsWithDesignationAndOwnerData.bankAccountsWithDesignationAndOwner
      .length
  ) {
    return (
      <>
        <LabeledDetail label={`Credit facility ${creditFacilityNumber}`}>
          {creditFacilityName}
        </LabeledDetail>
        <Box height="20px" />
        <Text>
          {`There are no bank accounts associated with ${creditFacilityName}.`}
        </Text>
        <Box height="40px" />
      </>
    );
  }

  return (
    <Flex direction="column" overflow="hidden" width="100%" gap="2rem">
      <LabeledDetail label={`Credit facility ${creditFacilityNumber}`}>
        {creditFacilityName}
      </LabeledDetail>
      <Flex overflowX="auto" width="100%">
        <Table variant="simple" size="md">
          <Thead
            background={colors.tableHeader}
            borderBottom="1px solid lightgrey"
            padding="8px 0"
          >
            <Tr>
              <Th minWidth="10rem">Account Holder</Th>
              <Th minWidth="12.5rem">Institution</Th>
              <Th minWidth="20rem">Account</Th>
              <Th minWidth="10rem">Classification</Th>
              <Th minWidth="10rem">Connection Provider</Th>
              <Th minWidth="10rem">Current Balance</Th>
              <Th minWidth="10rem">Available Balance</Th>
            </Tr>
          </Thead>
          <Tbody>
            {_.map(
              sortedBankAccountsWithDesignationAndOwnerData,
              (bankAccount) => {
                return (
                  <Sentry.ErrorBoundary
                    key={`key-${Math.random()}`}
                    onError={() => {
                      if (!toast.isActive(errorToastId)) {
                        errorToastConfig.title = `Error in showing bank account ${bankAccount.name}`;
                        toast(errorToastConfig);
                      }
                    }}
                  >
                    <BankAccountRow
                      bankAccount={bankAccount}
                      key={`key-${Math.random()}`}
                    />
                  </Sentry.ErrorBoundary>
                );
              },
            )}
          </Tbody>
        </Table>
      </Flex>
    </Flex>
  );
};
